import { JSX, ReactElement } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { Datagrid, Identifier, InfiniteList, WithRecord } from 'react-admin';
import { AdminMiniDescriptor } from '../admin-mini.types';

export interface AdminMiniResultsPanelProps {
    tool: AdminMiniDescriptor;
    /** The run whose results these are, or undefined when none is chosen. */
    run?: { id: Identifier, name: string };
    onOpenResult: (result: any) => void;
}

/** How a result's own status is coloured. Narrower than a run's: a result has more states. */
const STATUS_COLOURS: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
    pending: 'default',
    active: 'info',
    running: 'info',
    scoring: 'info',
    complete: 'success',
    out_of_time: 'warning',
    cancelled: 'warning',
    failed: 'error',
};

/**
 * Who answered a run, beside the list of runs.
 *
 * Shared between both tools. The recipient is read from `adminTarget`, which is the row an admin
 * typed rather than an `Employee` record: an admin run's targets are usually people who are in no
 * record anywhere, which is the whole point of the admin half.
 */
export const AdminMiniResultsPanel: (props: AdminMiniResultsPanelProps) => JSX.Element =
    (props: AdminMiniResultsPanelProps): JSX.Element => {
        const { tool, run } = props;

        return (
            <Box sx={ {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '12px',
                padding: '14px',
                minHeight: '320px',
            } }>
                <Typography sx={ {
                    fontSize: 'var(--wf-eyebrow)',
                    letterSpacing: 'var(--wf-track)',
                    textTransform: 'uppercase',
                    color: 'text.secondary',
                    fontWeight: 700,
                    marginBottom: '12px',
                } }>
                    { run === undefined ? 'Results' : run.name }
                </Typography>

                { run === undefined && (
                    <Typography sx={ { fontSize: '13px', color: 'text.secondary' } }>
                        { `Pick a ${ tool.runNoun } to see who answered.` }
                    </Typography>
                ) }

                { run !== undefined && (
                    <Box sx={ { maxHeight: '460px', overflowY: 'auto' } }>
                        <InfiniteList title=" "
                                      actions={ false }
                                      resource={ tool.resultResource }
                                      perPage={ 10 }
                                      filter={ { [tool.resultRunField]: run.id } }
                                      sort={ { field: 'id', order: 'DESC' } }>
                            <Datagrid bulkActionButtons={ false }
                                      rowClick={ (id: Identifier, resource: string, record: any): false => {
                                          props.onOpenResult(record);

                                          return false;
                                      } }>
                                <WithRecord label="Recipient" render={ (record: any): ReactElement => (
                                    <span>
                                        { record?.adminTarget?.name
                                            ?? record?.adminTarget?.email
                                            ?? `#${ record?.id }` }
                                    </span>
                                ) } />

                                <WithRecord label="Replied" render={ (record: any): ReactElement => {
                                    const messages: any[] = record?.emailMessages ?? record?.emails ?? [];
                                    const replied: boolean = messages.some(
                                        (message: any): boolean => message?.direction === 'inbound'
                                    );

                                    // Whether they wrote back is the first thing worth knowing:
                                    // a run collects replies, and one that got none has nothing
                                    // to score however healthy its status looks.
                                    return (
                                        <Chip size="small"
                                              variant="outlined"
                                              color={ replied ? 'success' : 'default' }
                                              label={ replied ? 'Replied' : 'No reply' } />
                                    );
                                } } />

                                <WithRecord label="Status" render={ (record: any): ReactElement => (
                                    <Chip size="small"
                                          variant="outlined"
                                          color={ STATUS_COLOURS[record?.status] ?? 'default' }
                                          label={ String(record?.status ?? 'unknown').replace('_', ' ') }
                                          sx={ { textTransform: 'capitalize' } } />
                                ) } />
                            </Datagrid>
                        </InfiniteList>
                    </Box>
                ) }
            </Box>
        );
    };
